import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, Trash2, X, Check, TestTube, Loader2, Eye, EyeOff, ClipboardPaste } from 'lucide-react';
import toast from 'react-hot-toast';
import { proxyService, Proxy, getErrorMessage } from '@/services';

const proxySchema = z.object({
  name: z.string().min(1, 'validation.required'),
  proxy_host: z.string().min(1, 'validation.required'),
  proxy_port: z.coerce.number().min(1).max(65535),
  proxy_username: z.string().optional(),
  proxy_password: z.string().optional(),
  proxy_type: z.enum(['http', 'https', 'socks5']),
  is_active: z.boolean(),
});

type ProxyForm = z.infer<typeof proxySchema>;

export default function Proxies() {
  const { t } = useTranslation();
  const [proxies, setProxies] = useState<Proxy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProxy, setEditingProxy] = useState<Proxy | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [importString, setImportString] = useState('');
  const [showImport, setShowImport] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProxyForm>({
    resolver: zodResolver(proxySchema),
    defaultValues: {
      is_active: true,
      proxy_port: 8080,
      proxy_type: 'http',
    },
  });

  const fetchProxies = async () => {
    try {
      const data = await proxyService.list();
      setProxies(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProxies();
  }, []);

  const parseProxyString = (input: string) => {
    const trimmed = input.trim();
    if (!trimmed) return;

    // Format: user:pass@host:port
    const atIndex = trimmed.lastIndexOf('@');
    if (atIndex === -1) {
      // Format: host:port (no auth)
      const parts = trimmed.split(':');
      if (parts.length >= 2) {
        const port = parseInt(parts[parts.length - 1], 10);
        const host = parts.slice(0, -1).join(':');
        if (host && !isNaN(port)) {
          setValue('proxy_host', host);
          setValue('proxy_port', port);
          setImportString('');
          setShowImport(false);
          toast.success(t('proxy.importSuccess'));
          return;
        }
      }
      toast.error(t('proxy.importError'));
      return;
    }

    const authPart = trimmed.substring(0, atIndex);
    const hostPart = trimmed.substring(atIndex + 1);

    // Parse auth: user:pass
    const colonIndex = authPart.indexOf(':');
    if (colonIndex === -1) {
      toast.error(t('proxy.importError'));
      return;
    }
    const username = authPart.substring(0, colonIndex);
    const password = authPart.substring(colonIndex + 1);

    // Parse host:port
    const hostParts = hostPart.split(':');
    if (hostParts.length < 2) {
      toast.error(t('proxy.importError'));
      return;
    }
    const port = parseInt(hostParts[hostParts.length - 1], 10);
    const host = hostParts.slice(0, -1).join(':');

    if (!host || isNaN(port)) {
      toast.error(t('proxy.importError'));
      return;
    }

    setValue('proxy_host', host);
    setValue('proxy_port', port);
    setValue('proxy_username', username);
    setValue('proxy_password', password);
    setImportString('');
    setShowImport(false);
    toast.success(t('proxy.importSuccess'));
  };

  const openCreateModal = () => {
    setEditingProxy(null);
    setShowPassword(false);
    setShowImport(false);
    setImportString('');
    reset({
      name: '',
      proxy_host: '',
      proxy_port: 8080,
      proxy_username: '',
      proxy_password: '',
      proxy_type: 'http',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (proxy: Proxy) => {
    setEditingProxy(proxy);
    setShowPassword(false);
    reset({
      name: proxy.name,
      proxy_host: proxy.proxy_host,
      proxy_port: proxy.proxy_port,
      proxy_username: proxy.proxy_username || '',
      proxy_password: proxy.proxy_password || '',
      proxy_type: proxy.proxy_type,
      is_active: proxy.is_active === 1,
    });
    setShowModal(true);
  };

  const onSubmit = async (data: ProxyForm) => {
    try {
      const payload = {
        name: data.name,
        proxy_host: data.proxy_host,
        proxy_port: data.proxy_port,
        proxy_username: data.proxy_username || undefined,
        proxy_password: data.proxy_password || undefined,
        proxy_type: data.proxy_type,
        is_active: data.is_active,
      };

      if (editingProxy) {
        await proxyService.update(editingProxy.id, payload);
        toast.success(t('common.updated'));
      } else {
        await proxyService.create(payload);
        toast.success(t('common.created'));
      }

      setShowModal(false);
      fetchProxies();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await proxyService.delete(id);
      toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchProxies();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleTest = async (id: number) => {
    setTestingId(id);
    try {
      const result = await proxyService.test(id);
      if (result.working) {
        toast.success(`${t('proxy.testSuccess')} (${result.latency}ms - ${result.ip})`);
      } else {
        toast.error(`${t('proxy.testFailed')}: ${result.error}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || t('proxy.testFailed'));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('proxy.title')}</h1>
          <p className="text-dark-400 mt-1">
            {proxies.length} {t('proxy.configuredCount')}
          </p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus size={18} />
          {t('proxy.add')}
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : proxies.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t('proxy.name')}</th>
                <th>{t('proxy.host')}</th>
                <th>{t('proxy.port')}</th>
                <th>{t('proxy.type')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {proxies.map((proxy) => (
                <tr key={proxy.id}>
                  <td className="font-medium text-dark-100">{proxy.name}</td>
                  <td>{proxy.proxy_host}</td>
                  <td>{proxy.proxy_port}</td>
                  <td className="uppercase text-xs">{proxy.proxy_type}</td>
                  <td>
                    <span
                      className={proxy.is_active ? 'badge-success' : 'badge-error'}
                    >
                      {proxy.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td>
                    {deleteConfirm === proxy.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(proxy.id)}
                          className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleTest(proxy.id)}
                          disabled={testingId === proxy.id}
                          className="p-1.5 rounded-lg hover:bg-primary-50 text-dark-400 hover:text-primary-600"
                          title={t('proxy.test')}
                        >
                          {testingId === proxy.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <TestTube size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => openEditModal(proxy)}
                          className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(proxy.id)}
                          className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="card w-full max-w-lg m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingProxy ? t('proxy.edit') : t('proxy.add')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            {/* Quick Import */}
            {!editingProxy && (
              <div className="mb-4">
                {!showImport ? (
                  <button
                    type="button"
                    onClick={() => setShowImport(true)}
                    className="text-sm text-primary-600 hover:text-primary-500 flex items-center gap-1.5"
                  >
                    <ClipboardPaste size={14} />
                    {t('proxy.quickImport')}
                  </button>
                ) : (
                  <div className="p-3 bg-dark-800 rounded-lg border border-dark-600 space-y-2">
                    <label className="text-xs font-medium text-dark-300">{t('proxy.importFormat')}</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={importString}
                        onChange={(e) => setImportString(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); parseProxyString(importString); } }}
                        className="input flex-1 text-sm font-mono"
                        placeholder="user:pass@host:port"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => parseProxyString(importString)}
                        className="btn-primary text-sm px-3"
                      >
                        {t('proxy.import')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowImport(false); setImportString(''); }}
                        className="p-2 rounded-lg hover:bg-dark-700 text-dark-400"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">{t('proxy.name')}</label>
                <input
                  type="text"
                  className={errors.name ? 'input-error' : 'input'}
                  placeholder="My Proxy"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.name.message || '')}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('proxy.host')}</label>
                  <input
                    type="text"
                    className={errors.proxy_host ? 'input-error' : 'input'}
                    placeholder="proxy.example.com"
                    {...register('proxy_host')}
                  />
                  {errors.proxy_host && (
                    <p className="mt-1 text-sm text-error-500">
                      {t(errors.proxy_host.message || '')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">{t('proxy.port')}</label>
                  <input
                    type="number"
                    className={errors.proxy_port ? 'input-error' : 'input'}
                    {...register('proxy_port')}
                  />
                  {errors.proxy_port && (
                    <p className="mt-1 text-sm text-error-500">
                      {t(errors.proxy_port.message || '')}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="label">{t('proxy.type')}</label>
                <select className="input" {...register('proxy_type')}>
                  <option value="http">HTTP</option>
                  <option value="https">HTTPS</option>
                  <option value="socks5">SOCKS5</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('auth.username')} ({t('common.optional')})</label>
                  <input
                    type="text"
                    className="input"
                    {...register('proxy_username')}
                  />
                </div>
                <div>
                  <label className="label">{t('auth.password')} ({t('common.optional')})</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input pr-10"
                      {...register('proxy_password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-200"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                  {...register('is_active')}
                />
                <label htmlFor="is_active" className="text-sm text-dark-200">
                  {t('common.active')}
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
